import React, { useEffect, useState } from 'react';
import { AddBox, HighlightOff } from '@mui/icons-material';
import styled from 'styled-components';
import { string, object, array, number } from 'yup';
import {
  useFormik,
  FieldArray,
  FormikProvider,
  type FieldArrayRenderProps,
  type FormikErrors,
} from 'formik';
import { PAGE_TEXT } from '../lang';
import { type DialogContentValue } from './types';
import { GPATypeOptions } from './options';
import { useConvertNonMatchedGPA, type Mark } from '../../../graphql';
import {
  Dialog,
  TextField,
  Button,
  Select,
  IconButton,
} from '../../../../../components';

type GPADialogProps = {
  dialogTitle: string;
  openDialog: boolean;
  dialogContent: DialogContentValue;
  viewMode: boolean;
  width?: string;
  onDialogApply?: (dialogContent: DialogContentValue) => void;
  onDialogClose: (dialogContent: DialogContentValue) => void;
};

const dialogText = PAGE_TEXT.QUALIFICATION.ACADEMIC_QUALIFICATION.GPA_DIALOG;
const GPATypeText = dialogText.GPA_TYPE_OPTIONS;

export const GPAConversionDialog: React.FunctionComponent<GPADialogProps> = (
  props: GPADialogProps,
) => {
  const {
    openDialog,
    dialogContent,
    dialogTitle,
    width = '800px',
    onDialogClose,
    onDialogApply = () => {},
    viewMode,
  } = props;
  const [openGPA, setOpenGPA] = useState(openDialog);
  const convertGPA = useConvertNonMatchedGPA();

  useEffect(() => {
    setOpenGPA(openDialog);
  }, [openDialog]);

  const handleClose = (): void => {
    setOpenGPA(false);
    onDialogClose(dialogFormik.values);
  };

  async function getConvertGPA(values: DialogContentValue): Promise<any> {
    const { courses, digitToLetterRule } = values;
    let processedCourses: Mark[] = [...courses];
    processedCourses = courses.map(course => ({
      ...course,
      result: course.result.toString(),
      credit:
        digitToLetterRule === GPATypeText.AVERAGE_MARK ? 1 : course.credit,
    }));

    const variables = {
      digitToLetterRule,
      courses: processedCourses,
    };
    const res = await convertGPA({
      variables,
    });
    return res.data?.convertGPA?.convertedGPA;
  }

  const handleConvertGPA = async (): Promise<void> => {
    const convertedGPA = await getConvertGPA(dialogFormik.values);
    if (convertedGPA !== undefined) {
      await dialogFormik.setFieldValue('officialMark', String(convertedGPA));
    }
  };

  const initialCourseArr: Mark[] = Array.from({ length: 3 }, () => ({
    course: '',
    credit: null,
    result: '',
  }));

  const hasCoursesHistory = Boolean(dialogContent.courses?.[0]?.course);

  const dialogFormik = useFormik<DialogContentValue>({
    initialValues: {
      officialMark: dialogContent?.officialMark ?? '0',
      courses: hasCoursesHistory ? dialogContent?.courses : initialCourseArr,
      digitToLetterRule: dialogContent?.digitToLetterRule ?? '',
    },
    validationSchema: object({
      digitToLetterRule: string().required('Required'),
      courses: array().of(
        object({
          course: string().required('Required'),
          credit: number().when(
            '$digitToLetterRule', // Use $ symbol to access context (higher-level value)
            ([digitToLetterRule], schema) => {
              return digitToLetterRule === GPATypeText.AVERAGE_MARK
                ? schema.notRequired()
                : schema.required('Required').min(0.1, 'Invalid number');
            },
          ),
          result: string()
            .required('Required')
            .test('is-valid-number', 'Invalid number', value => {
              // Regular expression to match strings that consist of digits with optional decimal point
              const numberRegex = /^[0-9]+(\.[0-9]+)?$/;
              return typeof value === 'string' && numberRegex.test(value);
            }),
        }),
      ),
    }),
    onSubmit: handleConvertGPA,
  });

  const handleConvertAndApply = async (): Promise<void> => {
    await dialogFormik.submitForm();

    // Validate the form
    const errors = await dialogFormik.validateForm();
    if (Object.keys(errors)?.length !== 0 || !dialogFormik.isValid) {
      // If there are errors, return early and do not proceed
      return;
    }
    const convertedGPA = await getConvertGPA(dialogFormik.values);

    if (convertedGPA as boolean) {
      await dialogFormik.setFieldValue('officialMark', String(convertedGPA));
      const { courses, digitToLetterRule } = dialogFormik.values;

      let processedCourses: Mark[] = [...courses];
      processedCourses = courses.map(course => ({
        ...course,
        result: course.result.toString(),
        credit:
          digitToLetterRule === GPATypeText.AVERAGE_MARK
            ? 1
            : (course.credit ?? 1),
      }));

      onDialogApply({
        officialMark: String(convertedGPA),
        courses: processedCourses,
        digitToLetterRule,
      });
    }
  };

  const handleAddCourse =
    (arrayHelpers: FieldArrayRenderProps): (() => void) =>
    () => {
      arrayHelpers.push({
        course: '',
        credit: null,
        result: '',
      });
    };

  const hideCredits =
    dialogFormik.values.digitToLetterRule === GPATypeText.AVERAGE_MARK;
  return (
    <Dialog
      dialogTitle={dialogTitle}
      openDialog={openGPA}
      onDialogClose={handleClose}
      displayCloseButton
      width={width}
    >
      <ContainerStyle>
        <h2
          style={{
            fontSize: '1rem',
            fontStyle: 'italic',
            padding: '0 0 0 0.5rem',
          }}
        >
          {dialogText.WARNING_TIPS}
        </h2>
        <h2 className="p-2 mb-2 font-semibold">
          {dialogText.HELPER_PAGE}
          <a
            className="underline decoration-1 cursor-pointer"
            href="/gpa-helperpage"
            target="_blank"
            rel="noopener"
          >
            here.
          </a>
        </h2>

        <FormikProvider value={dialogFormik}>
          <form onSubmit={dialogFormik.handleSubmit}>
            <div className="w-full pr-4 py-2">
              <Select
                fullWidth
                label={dialogText.GPA_TYPE}
                name="digitToLetterRule"
                options={GPATypeOptions}
                disabled={viewMode}
                value={dialogFormik.values.digitToLetterRule}
                onChange={dialogFormik.handleChange}
                InputLabelProps={{ shrink: true }}
                onBlur={dialogFormik.handleBlur}
                error={
                  dialogFormik.touched.digitToLetterRule &&
                  Boolean(dialogFormik.errors.digitToLetterRule)
                }
                helperText={
                  dialogFormik.touched.digitToLetterRule &&
                  dialogFormik.errors.digitToLetterRule
                }
              />
            </div>

            <div className="mt-2">
              <FieldArray
                name="courses"
                render={(arrayHelpers: FieldArrayRenderProps) => {
                  return (
                    <div>
                      {dialogFormik.values.courses?.map((courseItem, idx) => {
                        return (
                          <div
                            key={idx}
                            className={`grid ${
                              hideCredits
                                ? `${
                                    viewMode
                                      ? 'grid-cols-[4fr,3fr]'
                                      : 'grid-cols-[4fr,3fr,1fr]'
                                  }`
                                : `${
                                    viewMode
                                      ? 'grid-cols-[3fr,2fr,2fr]'
                                      : 'grid-cols-[3fr,2fr,2fr,1fr]'
                                  }`
                            } gap-4 justify-items-center content-start mb-4 mt-2 mr-4`}
                          >
                            <TextField
                              fullWidth
                              label={dialogText.COURSE_NAME}
                              name={`courses.${idx}.course`}
                              disabled={viewMode}
                              value={courseItem.course}
                              onChange={dialogFormik.handleChange}
                              InputLabelProps={{ shrink: true }}
                              onBlur={dialogFormik.handleBlur}
                              // workaround for a known formik issue for nested objects
                              // https://github.com/jaredpalmer/formik/issues/2347
                              error={
                                dialogFormik.touched.courses &&
                                dialogFormik.touched.courses[idx]?.course &&
                                Boolean(
                                  (
                                    (dialogFormik.errors.courses ?? [])[
                                      idx
                                    ] as FormikErrors<Mark>
                                  )?.course ?? false,
                                )
                              }
                              helperText={
                                dialogFormik.touched.courses &&
                                dialogFormik.touched.courses[idx]?.course &&
                                (
                                  (dialogFormik.errors.courses ?? [])[
                                    idx
                                  ] as FormikErrors<Mark>
                                )?.course
                              }
                            />
                            {!hideCredits && (
                              <TextField
                                fullWidth
                                disabled={viewMode}
                                label={dialogText.CREDITS}
                                name={`courses.${idx}.credit`}
                                value={courseItem.credit}
                                InputLabelProps={{ shrink: true }}
                                onChange={dialogFormik.handleChange}
                                onBlur={dialogFormik.handleBlur}
                                type="number"
                                error={
                                  dialogFormik.touched.courses &&
                                  dialogFormik.touched.courses[idx]?.credit &&
                                  Boolean(
                                    (
                                      (dialogFormik.errors.courses ?? [])[
                                        idx
                                      ] as FormikErrors<Mark>
                                    )?.credit ?? false,
                                  )
                                }
                                helperText={
                                  dialogFormik.touched.courses &&
                                  dialogFormik.touched.courses[idx]?.credit &&
                                  (
                                    (dialogFormik.errors.courses ?? [])[
                                      idx
                                    ] as FormikErrors<Mark>
                                  )?.credit
                                }
                              />
                            )}
                            <TextField
                              fullWidth
                              label={dialogText.RESULT}
                              name={`courses.${idx}.result`}
                              InputLabelProps={{ shrink: true }}
                              value={courseItem.result}
                              inputProps={{ inputMode: 'numeric' }} // keep the string type to align with the backend but only numeric input is accepted, achieve this by formik
                              onChange={dialogFormik.handleChange}
                              onBlur={dialogFormik.handleBlur}
                              disabled={viewMode}
                              error={
                                dialogFormik.touched.courses &&
                                dialogFormik.touched.courses[idx]?.result &&
                                Boolean(
                                  (
                                    (dialogFormik.errors.courses ?? [])[
                                      idx
                                    ] as FormikErrors<Mark>
                                  )?.result ?? false,
                                )
                              }
                              helperText={
                                dialogFormik.touched.courses &&
                                dialogFormik.touched.courses[idx]?.result &&
                                (
                                  (dialogFormik.errors.courses ?? [])[
                                    idx
                                  ] as FormikErrors<Mark>
                                )?.result
                              }
                            />
                            {!viewMode && (
                              <div>
                                <IconButton
                                  className="!mt-2"
                                  disabled={idx < 3}
                                  color="primary"
                                  icon={<HighlightOff />}
                                  onClick={() => {
                                    arrayHelpers.remove(idx);
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <div className="flex justify-between">
                        <div className="flex flex-row items-center">
                          {!viewMode && (
                            <Button
                              variant="text"
                              sx={{ textTransform: 'none' }}
                              onClick={handleAddCourse(arrayHelpers)}
                              disabled={
                                dialogFormik.values.digitToLetterRule === ''
                              }
                              type="submit"
                              startIcon={<AddBox />}
                            >
                              {dialogText.ADD_MORE_COURSE}
                            </Button>
                          )}
                        </div>
                        <div className="p-4 mr-2">
                          {dialogText.AVE_SCORE +
                            ': ' +
                            dialogFormik.values.officialMark}
                        </div>
                      </div>

                      <div className="button-area">
                        {!viewMode && (
                          <Button
                            className="!ml-4"
                            variant="contained"
                            disabled={!dialogFormik.values.courses?.length}
                            onClick={handleConvertAndApply}
                          >
                            {PAGE_TEXT.CALCULATE}
                          </Button>
                        )}
                        <Button
                          variant="text"
                          onClick={handleClose}
                          className="!ml-4"
                        >
                          {viewMode ? PAGE_TEXT.CLOSE : PAGE_TEXT.CANCEL}
                        </Button>
                      </div>
                    </div>
                  );
                }}
              />
            </div>
          </form>
        </FormikProvider>
      </ContainerStyle>
    </Dialog>
  );
};

const ContainerStyle = styled.div`
  margin: 0em 2em 1em;

  .button-area {
    margin: 2rem 0 1rem;
    display: flex;
    flex-direction: row;
    justify-content: center;
  }
`;
